import { Button, Pagination, Spinner } from '~/ui';
import { t } from 'i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useEffect, useRef, useState } from 'react';
import SelectedIcon from './icons/selected-icon.svg?react';
import PhotoIcon from '~/assets/svg/newSvg/picture.svg?react';
import styles from './AssetImageContainer.module.scss';
import classNames from 'classnames';

interface IImage {
  id: number;
  full_photo_url: string;
  path: string;
}

interface IArticlesProps {
  multiselect?: boolean;
  selectedImage: IImage;
  assetId: number;
  module: string;
  queryKey: string;
  onCloseModal: () => void;
  onSelectImages: (values: IImage) => void;
  onSelectImagesArray?: (values: IImage[] | []) => void;
}

const AssetImageContainer = ({
  selectedImage,
  onCloseModal,
  onSelectImages,
  module,
  queryKey,
  assetId,
}: IArticlesProps) => {
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<IImage>(selectedImage);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<IImage[]>([]);

  const handleSubmit = () => {
    onSelectImages(selectedId);
    onCloseModal();
  };

  const handleImageSelect = (value: IImage) => {
    setSelectedId(value);
  };

  const handleClose = () => {
    onCloseModal();
    setSelectedId(selectedImage);
  };

  const { data: images, isLoading } = useQuery({
    queryFn: async () => {
      if (!assetId) return { data: [], last_page: 1 };
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${assetId}`,
        { params: { perPage: 9, page } }
      );
      return data;
    },
    enabled: !!assetId,
    queryKey: [queryKey, module, page, assetId],
    onError: error => {
      toastError(error);
    },
  });

  const { mutateAsync: uploadPhotos } = useMutation(
    async (files: File[]) => {
      const formData = new FormData();
      files.forEach(file => formData.append('photos[]', file));
      const { data } = await instance.post('admin/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data as IImage[];
    },
    { onError: error => toastError(error) }
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setIsUploading(true);
    try {
      const uploaded = await uploadPhotos(Array.from(files));
      if (uploaded?.length) {
        setSelectedId({
          id: uploaded[0].id,
          full_photo_url: uploaded[0].full_photo_url,
          path: '',
        });
        setUploadedPhotos(prev => [...uploaded.map(p => ({ ...p, path: '' })), ...prev]);
        toastSuccess(t('photo_uploaded'));
      }
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Маппинг photo_storage → формат IImage
  const storageImages: IImage[] = (images?.data || []).map(
    (photo: { id: number; photo_id: number; url: string }) => ({
      id: photo.photo_id,
      full_photo_url: photo.url,
      path: '',
    })
  );

  const uploadedIds = new Set(uploadedPhotos.map(p => p.id));
  const mappedImages = [...uploadedPhotos, ...storageImages.filter(p => !uploadedIds.has(p.id))];

  useEffect(() => {
    setSelectedId(selectedImage);
  }, [selectedImage]);

  return (
    <div className={styles.photoContainer}>
      <div className={styles.headingWrapper}>
        <p className={styles.title}>{t('choose_photo')}</p>
        <div className={styles.closeBtnWrapper}>
          {selectedId.full_photo_url && (
            <Button
              text={t('discard')}
              className={styles.resetBtn}
              onClick={() =>
                setSelectedId({ id: 0, full_photo_url: '', path: '' })
              }
            />
          )}
          <Button
            text={t('close')}
            className={styles.closeBtn}
            onClick={handleClose}
          />
        </div>
      </div>
      <div className={styles.uploadSection}>
        <button
          type="button"
          className={styles.uploadButton}
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <Spinner />
          ) : (
            <>
              <PhotoIcon />
              <span>{t('upload_photos')}</span>
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className={styles.hiddenInput}
        />
      </div>
      <div className={styles.imagesWrapper}>
        <ul className={!isLoading ? styles.imagesList : styles.loadingList}>
          {isLoading ? (
            <Spinner />
          ) : mappedImages.length ? (
            mappedImages.map((item: IImage) => (
              <li key={item.id} className={styles.imagesItem}>
                <button
                  type="button"
                  onClick={() => handleImageSelect(item)}
                  onDoubleClick={e => e.preventDefault()}
                  className={classNames(
                    styles.imageBtn,
                    selectedId.id === item.id && styles.activeImage
                  )}
                >
                  <img
                    src={item.full_photo_url}
                    alt={item.path}
                    className={classNames(styles.image)}
                    onDoubleClick={e => e.preventDefault()}
                  />
                  {selectedId.id === item.id && (
                    <SelectedIcon className={styles.selectedIcon} />
                  )}
                </button>
              </li>
            ))
          ) : (
            <p style={{ textAlign: 'center', padding: '20px', color: '#888' }}>{t('no_photos')}</p>
          )}
        </ul>
        <Pagination
          pageCount={images?.last_page}
          onPageChange={(selectedPage: { selected: number }) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
      <Button
        text={t('confirm')}
        className={styles.submitBtn}
        type="button"
        onClick={handleSubmit}
        disabled={selectedId.full_photo_url ? false : true}
      />
    </div>
  );
};

export default AssetImageContainer;
